import { useState } from 'react';
import styled from 'styled-components';

import MaterialIcon from '@/components/atom/MaterialIcon';
import theme from '@/styles/theme';
import { QnaAnswerReviewVo } from '@/types/qna';

interface Props {
  review: QnaAnswerReviewVo;
}

const AnswerReview = ({ review }: Props) => {
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const numberWithDecimal = review.rating.toFixed(1);

  const [tag, ...restTags] = review.reviewTags;

  return (
    <Wrapper>
      <ScoreWrapper>
        <IconWrapper>
          {Array.from({ length: 5 }).map((_, i) => {
            const isActive = i + 1 <= review.rating;
            return (
              <MaterialIcon
                key={`review-star-${review.id}-${i}`}
                name={'star'}
                size={14}
                filled
                color={isActive ? theme.color.yellow500 : theme.color.gray100}
              />
            );
          })}
        </IconWrapper>
        <ScoreText>{numberWithDecimal}</ScoreText>
      </ScoreWrapper>
      <ReviewWritingDate>{review.createdAt}</ReviewWritingDate>
      <ReviewContent>{review.content}</ReviewContent>
      <TagList isOpen={isTagsOpen}>
        {isTagsOpen ? (
          review.reviewTags.map((tag) => (
            <ReviewTagItem key={`review-tags-${review.id}-${tag.id}`}>
              {tag.text}
            </ReviewTagItem>
          ))
        ) : (
          <>
            <ReviewTagItem>{tag.text}</ReviewTagItem>
            {restTags.length > 0 && (
              <ReviewTagItem onClick={() => setIsTagsOpen(true)}>
                {`+${restTags.length}`}
              </ReviewTagItem>
            )}
          </>
        )}
      </TagList>
    </Wrapper>
  );
};

export default AnswerReview;

const Wrapper = styled.div`
  margin-top: 20px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.color.gray30};
  display: flex;
  flex-direction: column;
`;

const IconWrapper = styled.div`
  display: flex;
  gap: 1.75px;
`;

const ScoreWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
`;

const ScoreText = styled.p`
  ${({ theme }) => theme.typography.UI14B}
`;

const ReviewWritingDate = styled.p`
  ${({ theme }) => theme.typography.UI12R}
  color: ${({ theme }) => theme.color.gray300};
  margin-bottom: 8px;
`;

const ReviewContent = styled.p`
  ${({ theme }) => theme.typography.UI15R}
  color: ${({ theme }) => theme.color.gray800};
  margin-bottom: 15px;
`;

const TagList = styled.ul<{ isOpen: boolean }>`
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;
  gap: 8px;
  max-height: ${({ isOpen }) => (isOpen ? '100%' : '28px')};
`;

const ReviewTagItem = styled.li`
  ${({ theme }) => theme.typography.UI13M}
  color: ${({ theme }) => theme.color.gray400};
  background-color: ${({ theme }) => theme.color.gray10};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  padding: 0 8px;
  height: 28px;
`;
